import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken as storeAccessToken,
  setRefreshToken as storeRefreshToken,
  clearTokens,
} from "./tokenStorage";
import { api } from "../http/axiosClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(getAccessToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function bootstrap() {
      const access = getAccessToken();
      const refresh = getRefreshToken();

      if (!access && !refresh) {
        if (!alive) return;
        setUser(null);
        setAccessToken(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const res = await api.get("/auth/me");

        if (!alive) return;

        setUser(res.data);
        setAccessToken(getAccessToken());
      } catch {
        if (!alive) return;

        clearTokens();
        setUser(null);
        setAccessToken(null);
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      alive = false;
    };
  }, []);

  async function signIn(email, password) {
    const body = new URLSearchParams();
    body.append("grant_type", "password");
    body.append("username", email);
    body.append("password", password);

    const res = await api.post("/auth/login", body, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    const newAccessToken = res.data.access_token ?? res.data.accessToken;
    const newRefreshToken = res.data.refresh_token ?? res.data.refreshToken;

    storeAccessToken(newAccessToken);
    setAccessToken(newAccessToken);

    if (newRefreshToken) {
      storeRefreshToken(newRefreshToken);
    }

    const me = await api.get("/auth/me");
    setUser(me.data);

    return res.data;
  }

  function signOut() {
    clearTokens();
    setAccessToken(null);
    setUser(null);
  }

  const value = useMemo(() => {
    return {
      accessToken,
      user,
      loading,
      isAuthed: !!user,
      signIn,
      signOut,
    };
  }, [accessToken, user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return ctx;
}